import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
	Navigation,
	AlertMessage,
	InlineAlertMessage,
	ModalShell,
	PoolExhaustionWarningModal,
	useToast,
} from '@quizzach/components';
import { usePremadeQuizzes, useQuizValidityChecker } from '@quizzach/api-hooks';
import {
	useConfigurator,
	useToastClearance,
	MIN_QUESTIONS,
	MAX_QUESTIONS,
} from '@quizzach/hooks';
import {
	ApiFailedReason,
	CategoryGeneratorState,
	EnumQuizType,
	RoutePath,
	SubcategoryGeneratorState,
} from '@quizzach/interfaces';
import {
	IcAlertTriangle,
	IcArrowRight,
	IcCheck,
	IcClose,
} from '@quizzach/assets';
import { Reveal } from '@quizzach/motion';
import {
	BtnTeal,
	CfgHead,
	CfgPage,
	CfgRow,
	Sk,
	KAddBtn,
	KAvRow,
	KBar,
	KChip,
	KCkRow,
	KKRow,
	KLab,
	KMeter,
	KModRow,
	KRmx,
	KSeg,
	KSRow,
	KStp,
	KTick,
} from '@quizzach/styles';

const PREMADE_HINT_KEY = 'generatorPremadeHintShown';

const LEVEL_TONES: Record<number, 'teal' | 'gold' | 'coral'> = {
	1: 'teal',
	2: 'gold',
	3: 'coral',
};

const Stepper = ({
	value,
	onStep,
	onSet,
	startRepeat,
	stopRepeat,
}: {
	value: number;
	onStep: (delta: number) => void;
	onSet: (value: number) => void;
	startRepeat: (step: () => void) => void;
	stopRepeat: () => void;
}) => {
	// Draft holds what the user is typing so an empty field or an
	// over-the-max value isn't clamped mid-keystroke; commit on blur/Enter.
	const [draft, setDraft] = useState<string | null>(null);

	const commit = () => {
		// an empty field is an abandoned edit — revert to the current value.
		// Committing it as 0 would deselect a subcategory row (and wipe its
		// levels) under a user who just cleared the input to retype.
		if (draft !== null && draft !== '') onSet(parseInt(draft, 10));
		setDraft(null);
	};

	return (
		<KStp>
			<button
				type='button'
				aria-label='-'
				style={{ touchAction: 'manipulation' }}
				onContextMenu={(e) => e.preventDefault()}
				onPointerDown={() => startRepeat(() => onStep(-1))}
				onPointerUp={stopRepeat}
				onPointerLeave={stopRepeat}
				onPointerCancel={stopRepeat}
			>
				-
			</button>
			<input
				className='n'
				type='text'
				inputMode='numeric'
				value={draft ?? String(value)}
				onChange={(e) =>
					setDraft(e.target.value.replace(/\D/g, '').slice(0, 3))
				}
				onFocus={(e) => e.target.select()}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === 'Enter') e.currentTarget.blur();
				}}
			/>
			<button
				type='button'
				aria-label='+'
				style={{ touchAction: 'manipulation' }}
				onContextMenu={(e) => e.preventDefault()}
				onPointerDown={() => startRepeat(() => onStep(1))}
				onPointerUp={stopRepeat}
				onPointerLeave={stopRepeat}
				onPointerCancel={stopRepeat}
			>
				+
			</button>
		</KStp>
	);
};

const Configurator = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const cfg = useConfigurator();
	const { initiateAddToCart } = useQuizValidityChecker();

	const { premadeTags, getPremadeTags } = usePremadeQuizzes();

	const [submitting, setSubmitting] = useState(false);
	const [showChangesNeeded, setShowChangesNeeded] = useState(false);
	const [showPremadeHint, setShowPremadeHint] = useState(false);
	const { showToast } = useToast();
	const kbarRef = useToastClearance();

	const levelLabel = (level: number) =>
		level === 1
			? t('premade.single.levelEasy')
			: level === 2
				? t('premade.single.levelMedium')
				: t('premade.single.levelHard');

	// an empty subcategory-mode category acts as unselected, so the
	// one-category premade hint counts only categories that contribute
	// questions
	const contributingCategories = cfg.selected.filter(
		(category) => cfg.categoryTotal(category) > 0
	);
	const singleCategory =
		contributingCategories.length === 1 ? contributingCategories[0] : null;

	const handleContinue = () => {
		if (!cfg.canContinue || submitting) return;
		// A one-category quiz overlaps with the premade special editions, so
		// suggest those once per session before generating.
		if (singleCategory && !sessionStorage.getItem(PREMADE_HINT_KEY)) {
			sessionStorage.setItem(PREMADE_HINT_KEY, '1');
			// preload the tag list so the hint can deep-link to a matching tag page
			getPremadeTags();
			setShowPremadeHint(true);
			return;
		}
		submitQuiz();
	};

	const openPremadeEditions = () => {
		const tag = premadeTags.find(
			(name) => name.toLowerCase() === singleCategory?.name.toLowerCase()
		);
		navigate(
			tag
				? `${RoutePath.PremadeQuizzes}/tag/${encodeURIComponent(tag)}`
				: RoutePath.PremadeQuizzes
		);
	};

	const submitQuiz = async () => {
		if (submitting) return;
		setSubmitting(true);
		setShowChangesNeeded(false);

		try {
			const result = await initiateAddToCart();

			if (result.status) {
				navigate(RoutePath.GeneratorSummary, {
					state: { source: EnumQuizType.generator },
				});
				return;
			}
			if ('rejectionType' in result && result.rejectionType) {
				// re-sync against fresh availability so the inline max-errors
				// point at the affected category, then explain via popup
				await cfg.reinitializeFromSession();
				setShowChangesNeeded(true);
				return;
			}
			if ('conflict' in result && result.conflict) {
				showToast({
					severity: 'error',
					message: t('error.conflict'),
				});
				return;
			}
			// logging in is part of the flow, not an error — after login the user
			// lands back here with their selection restored and continues themselves
			if ('redirectedToLogin' in result && result.redirectedToLogin) return;
			showToast({
				severity: 'error',
				message: t('error.addToCartShort'),
			});
		} finally {
			setSubmitting(false);
		}
	};

	const renderChips = (levels: number[], onToggle: (level: number) => void) => (
		<span className='kchips'>
			{[1, 2, 3].map((level) => (
				<KChip
					key={level}
					type='button'
					$on={levels.includes(level)}
					$tone={LEVEL_TONES[level]}
					onClick={() => onToggle(level)}
				>
					{levelLabel(level)}
				</KChip>
			))}
		</span>
	);

	const renderCategoryMode = (category: CategoryGeneratorState) => (
		<KKRow>
			<KLab>{t('quiz.creation.difficulty')}</KLab>
			{renderChips(category.levels, (level) =>
				cfg.toggleCategoryLevel(category.id, level)
			)}
			<KLab>{t('quiz.creation.questionCount')}</KLab>
			<Stepper
				value={category.question_count ?? 0}
				onStep={(delta) => cfg.stepCategoryCount(category.id, delta)}
				onSet={(count) => cfg.setCategoryCount(category.id, count)}
				startRepeat={cfg.startRepeat}
				stopRepeat={cfg.stopRepeat}
			/>
			{category.levels.length === 0 && (
				<InlineAlertMessage
					type='error'
					message={t('quiz.creation.pickAtLeastOneDifficulty')}
					style={{ width: '100%', margin: '2px 0 0' }}
				/>
			)}
			{(category.question_count ?? 0) === 0 && (
				<InlineAlertMessage
					type='error'
					message={t('generator.redesign.addAtLeastOne')}
					style={{ width: '100%', margin: '2px 0 0' }}
				/>
			)}
			{category.invalid && (category.question_count ?? 0) !== 0 && (
				<InlineAlertMessage
					type='error'
					message={t('quiz.creation.availableQuestionsMax', {
						max: category.maximum ?? 0,
					})}
					style={{ width: '100%', margin: '2px 0 0' }}
				/>
			)}
		</KKRow>
	);

	const renderSubMode = (category: CategoryGeneratorState) => (
		<>
			{category.subcategories.map((sub: SubcategoryGeneratorState) => {
				const active = sub.question_count !== null;
				if (!active) {
					return (
						<KAvRow key={sub.id}>
							<span className='sname'>{sub.name}</span>
							<KAddBtn
								type='button'
								onClick={() => cfg.addSub(category.id, sub.id)}
							>
								+ {t('generator.redesign.addSub')}
							</KAddBtn>
						</KAvRow>
					);
				}
				return (
					<KSRow key={sub.id}>
						<span className='sname'>{sub.name}</span>
						{renderChips(sub.levels ?? [], (level) =>
							cfg.toggleSubLevel(category.id, sub.id, level)
						)}
						<Stepper
							value={sub.question_count ?? 0}
							onStep={(delta) => cfg.stepSubCount(category.id, sub.id, delta)}
							onSet={(count) => cfg.setSubCount(category.id, sub.id, count)}
							startRepeat={cfg.startRepeat}
							stopRepeat={cfg.stopRepeat}
						/>
						<KRmx
							type='button'
							className='krmx'
							aria-label={t('generator.redesign.removeSub')}
							onClick={() => cfg.removeSub(category.id, sub.id)}
						>
							<IcClose size={15} />
						</KRmx>
						{(sub.levels ?? []).length === 0 && (
							<InlineAlertMessage
								type='error'
								className='warn'
								message={t('quiz.creation.pickAtLeastOneDifficulty')}
								style={{ margin: 0 }}
							/>
						)}
						{sub.invalid && (sub.question_count ?? 0) !== 0 && (
							<InlineAlertMessage
								type='error'
								className='warn'
								message={t('quiz.creation.availableQuestionsMax', {
									max: sub.maximum ?? 0,
								})}
								style={{ margin: 0 }}
							/>
						)}
					</KSRow>
				);
			})}
		</>
	);

	const countOk = cfg.total >= MIN_QUESTIONS && cfg.total <= MAX_QUESTIONS;

	return (
		<CfgPage>
			<Navigation />

			<PoolExhaustionWarningModal
				show={showPremadeHint}
				onConfirm={(confirmed) => {
					setShowPremadeHint(false);
					if (confirmed) openPremadeEditions();
				}}
				onCancel={() => {
					setShowPremadeHint(false);
					submitQuiz();
				}}
				text={t('generator.redesign.premadeHint', {
					category: singleCategory?.name ?? '',
				})}
				cancelLabel={t('quiz.quick.continue')}
				confirmLabel={t('quiz.quick.viewSpecialEditions')}
			/>

			<CfgHead>
				{/* homepage-hero treatment: title lines rise out of an overflow
				    clip on load (styles live on CfgHead) */}
				<h1>
					<span className='row'>
						<span>{t('generator.redesign.title1')}</span>
					</span>
					<span className='row'>
						<span>
							<em>{t('generator.redesign.title2')}</em>
						</span>
					</span>
				</h1>
				<span className='hand'>{t('generator.redesign.hand')}</span>
			</CfgHead>

			{cfg.error && (
				<div style={{ padding: '0 var(--pad) 40px' }}>
					<AlertMessage code={ApiFailedReason.default} type='error' />
				</div>
			)}
			<ModalShell
				show={showChangesNeeded}
				onClose={() => setShowChangesNeeded(false)}
				size='sm'
				center
				icon={<IcAlertTriangle size={28} />}
				actions={
					<BtnTeal
						type='button'
						$small
						onClick={() => setShowChangesNeeded(false)}
					>
						{t('generator.redesign.changesOk')}
					</BtnTeal>
				}
			>
				<p style={{ margin: '8px 0 0' }}>{t('error.customizer')}</p>
			</ModalShell>

			<Reveal>
				{cfg.loading && (
					<div aria-hidden='true' style={{ padding: '0 0 40px' }}>
						{Array.from({ length: 5 }).map((_, index) => (
							<Sk
								key={index}
								style={{ height: 76, margin: '0 var(--pad) 16px' }}
							/>
						))}
					</div>
				)}
				{!cfg.loading &&
					cfg.fetched.map((category) => {
						const selectedCategory = cfg.getSelected(category.id);
						const selected = Boolean(selectedCategory);
						const catTotal = selectedCategory
							? cfg.categoryTotal(selectedCategory)
							: 0;
						const mode = cfg.modes[category.id] ?? 'category';

						return (
							<CfgRow key={category.id} $selected={selected}>
								<div
									className='head'
									onClick={() => cfg.toggleCategory(category.id)}
								>
									<KTick $on={selected} as='span'>
										<IcCheck size={16} />
									</KTick>
									<h3>{category.name}</h3>
									{catTotal > 0 && (
										<span className='cavail act'>
											{t('generator.redesign.selectedCount', {
												count: catTotal,
											})}
										</span>
									)}
								</div>
								{selected && selectedCategory && (
									<div className='cfg'>
										<KModRow>
											<KLab>{t('quiz.creation.selectionBy')}</KLab>
											<KSeg>
												<button
													type='button'
													className={mode === 'category' ? 'on' : ''}
													onClick={() => cfg.setMode(category.id, 'category')}
												>
													{t('quiz.creation.category')}
												</button>
												<button
													type='button'
													className={mode === 'subcategory' ? 'on' : ''}
													onClick={() =>
														cfg.setMode(category.id, 'subcategory')
													}
												>
													{t('quiz.creation.subcategory')}
												</button>
											</KSeg>
										</KModRow>
										{mode === 'category'
											? renderCategoryMode(selectedCategory)
											: renderSubMode(selectedCategory)}
									</div>
								)}
							</CfgRow>
						);
					})}
			</Reveal>

			<KBar ref={kbarRef}>
				<div className='left'>
					<span className={`tot ${countOk ? 'ok' : ''}`}>
						{t('generator.redesign.totalLabel')} <em>{cfg.total}</em> /{' '}
						{MAX_QUESTIONS}
					</span>
					<KMeter $ok={countOk}>
						<i
							style={{
								width: `${Math.min(100, (cfg.total / MAX_QUESTIONS) * 100)}%`,
							}}
						/>
						<span
							className='mk'
							style={{ left: `${(MIN_QUESTIONS / MAX_QUESTIONS) * 100}%` }}
						>
							<span>
								{t('generator.redesign.minLabel', { min: MIN_QUESTIONS })}
							</span>
						</span>
					</KMeter>
				</div>
				<div className='right'>
					<KCkRow onClick={cfg.selectAll}>
						<KTick $on={cfg.allSelected} as='span'>
							<IcCheck size={16} />
						</KTick>
						<span className='t'>{t('quiz.creation.selectAll')}</span>
					</KCkRow>
					<BtnTeal
						type='button'
						$small
						disabled={!cfg.canContinue || submitting}
						onClick={handleContinue}
					>
						{t('quiz.creation.next')} <IcArrowRight size={16} />
					</BtnTeal>
				</div>
			</KBar>
		</CfgPage>
	);
};

/** Legacy step-1 route now folds into the single-page configurator. */
export { Configurator as GeneratorQuizCustomizer };
